'use client';
import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

interface DropdownItem {
  label: string;
  href: string;
}

interface NavItem {
  label: string;
  href: string;
  hasDropdown?: boolean;
  dropdownItems?: DropdownItem[];
  megaDropdown?: {
    groups: {
      heading: string;
      items: DropdownItem[];
    }[];
  };
}

const navItems: NavItem[] = [
  { label: 'Home', href: '/' },
  {
    label: 'About Us',
    href: '/about',
    hasDropdown: true,
    dropdownItems: [
      { label: 'About UNOC', href: '/about' },
      { label: 'Our Mandate', href: '/about/mandate' },
      { label: 'Leadership Team', href: '/leadership' },
      { label: 'Subsidiaries', href: '/about/subsidiaries' },
    ],
  },
  {
    label: 'Our Business',
    href: '/projects',
    hasDropdown: true,
    megaDropdown: {
      groups: [
        {
          heading: 'Upstream',
          items: [
            { label: 'Exploration', href: '/projects' },
            { label: 'Kingfisher', href: '/projects/kingfisher' },
            { label: 'Tilenga', href: '/projects/tilenga' },
          ],
        },
        {
          heading: 'Midstream',
          items: [
            { label: 'EACOP', href: '/projects/eacop' },
            { label: 'Industrial Park', href: '/projects/industrial-park' },
            { label: 'Refinery', href: '/projects/refinery' },
          ],
        },
        {
          heading: 'Downstream',
          items: [
            { label: 'Bulk Trading', href: '/projects/bulk-trading' },
            { label: 'Uganda Oil Refinery', href: '/projects/refinery' },
          ],
        },
      ],
    },
  },
  {
    label: 'Sustainability',
    href: '/sustainability',
    hasDropdown: true,
    dropdownItems: [
      { label: 'Overview', href: '/sustainability' },
      { label: 'National Content', href: '/sustainability/national-content' },
      { label: 'Health, Safety & Environment', href: '/sustainability/hse' },
      { label: 'Environment & ESG', href: '/sustainability/environment' },
      { label: 'Community Impact', href: '/sustainability/community' },
    ],
  },
  { label: 'Media', href: '/news' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [mobileBusinessOpen, setMobileBusinessOpen] = useState(false);
  const [mobileSustainabilityOpen, setMobileSustainabilityOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [pathname]);

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 bg-white"
      style={{
        boxShadow: scrolled ? '0 2px 20px rgba(0,0,0,0.08)' : 'none',
        transition: 'box-shadow 0.3s ease',
      }}
    >
      {/* Top bar */}
      <div className="hidden md:block bg-[#F4F4F4] border-b border-gray-200">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="flex items-center justify-between h-10 text-xs">
            <div className="flex items-center gap-6">
              <a
                href="tel:+256312444600"
                className="flex items-center gap-2 text-gray-700 hover:text-[#CC0000] transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#CC0000]" aria-hidden="true">
                  <path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384" />
                </svg>
                <span className="font-medium">+256 312 444 600</span>
              </a>
              <span className="text-gray-300">|</span>
              <a
                href="mailto:info.unoc@unoc.co.ug"
                className="flex items-center gap-2 text-gray-700 hover:text-[#CC0000] transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#CC0000]" aria-hidden="true">
                  <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7" />
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                </svg>
                <span className="font-medium">info.unoc@unoc.co.ug</span>
              </a>
            </div>
            <div className="flex items-center gap-5">
              <button className="flex items-center gap-1.5 text-gray-700 hover:text-[#CC0000] transition-colors text-xs font-medium">
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
                  <path d="M2 12h20" />
                </svg>
                EN
                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>
              <div className="flex items-center gap-3 pl-3 border-l border-gray-300">
                <a href="https://www.facebook.com/UNOCLimited" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#CC0000] transition-colors" aria-label="Facebook">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                  </svg>
                </a>
                <a href="https://www.linkedin.com/company/uganda-national-oil-company-limited" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#CC0000] transition-colors" aria-label="LinkedIn">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z" />
                    <rect x="2" y="9" width="4" height="12" />
                    <circle cx="4" cy="4" r="2" />
                  </svg>
                </a>
                <a href="https://twitter.com/UNOC_LTD" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#CC0000] transition-colors" aria-label="Twitter">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M22 4s-.7 2.1-2 3.4c1.6 10-9.4 17.3-18 11.6 2.2.1 4.4-.6 6-2C3 15.5.5 9.6 3 5c2.2 2.6 5.6 4.1 9 4-.9-4.2 4-6.6 7-3.8 1.1 0 3-1.2 3-1.2z" />
                  </svg>
                </a>
                <a href="https://www.youtube.com/@UNOCLimited" target="_blank" rel="noopener noreferrer" className="text-gray-600 hover:text-[#CC0000] transition-colors" aria-label="YouTube">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17" />
                    <path d="m10 15 5-3-5-3z" />
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="bg-white">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="flex items-center justify-between h-20" ref={dropdownRef}>
            {/* Logo */}
            <Link href="/" className="flex items-center gap-3 flex-shrink-0">
              <img
                src="/assets/images/unoc_logo-1778151064024.png"
                alt="UNOC - Uganda National Oil Company logo"
                className="h-10 w-auto object-contain"
              />
            </Link>

            {/* Desktop nav links */}
            <div className="hidden lg:flex items-center justify-center gap-1 flex-1 mx-12">
              {navItems.map((item) => (
                <div key={item.label} className="relative">
                  {item.megaDropdown ? (
                    <div
                      onMouseEnter={() => setOpenDropdown(item.label)}
                      onMouseLeave={() => setOpenDropdown(null)}
                    >
                      <Link
                        href={item.href}
                        className={`flex items-center gap-1 px-4 py-2 text-[15px] font-medium transition-colors ${
                          pathname === item.href || pathname?.startsWith(item.href + '/')
                            ? 'text-[#CC0000]' : 'text-[#0F0F0F] hover:text-[#CC0000]'
                        }`}
                      >
                        {item.label}
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform duration-200 ${openDropdown === item.label ? 'rotate-180' : ''}`} aria-hidden="true">
                          <path d="m6 9 6 6 6-6" />
                        </svg>
                      </Link>
                      {openDropdown === item.label && (
                        <div className="absolute top-full left-0 mt-0 bg-white border border-gray-200 shadow-lg z-50 min-w-[480px]">
                          <div className="grid grid-cols-3 gap-0 p-6">
                            {item.megaDropdown.groups.map((group) => (
                              <div key={group.heading} className="pr-6 last:pr-0">
                                <h4 className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#CC0000] mb-3 font-heading">
                                  {group.heading}
                                </h4>
                                <ul className="space-y-1">
                                  {group.items.map((sub) => (
                                    <li key={sub.label}>
                                      <Link
                                        href={sub.href}
                                        className="flex items-center gap-2 py-1.5 text-sm text-[#0F0F0F] hover:text-[#CC0000] transition-colors"
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-[#CC0000] flex-shrink-0" aria-hidden="true">
                                          <path d="M7 7h10v10" />
                                          <path d="M7 17 17 7" />
                                        </svg>
                                        {sub.label}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>
                          <div className="border-t border-gray-100 px-6 py-3 bg-[#F8F9FA]">
                            <Link
                              href="/projects"
                              className="text-xs font-bold text-[#CC0000] uppercase tracking-[0.15em] hover:text-[#AA0000] transition-colors font-heading"
                            >
                              View All Projects →
                            </Link>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : item.dropdownItems ? (
                    <div
                      onMouseEnter={() => setOpenDropdown(item.label)}
                      onMouseLeave={() => setOpenDropdown(null)}
                    >
                      <Link
                        href={item.href}
                        className={`flex items-center gap-1 px-4 py-2 text-[15px] font-medium transition-colors ${
                          pathname === item.href || pathname?.startsWith(item.href + '/')
                            ? 'text-[#CC0000]' : 'text-[#0F0F0F] hover:text-[#CC0000]'
                        }`}
                      >
                        {item.label}
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform duration-200 ${openDropdown === item.label ? 'rotate-180' : ''}`} aria-hidden="true">
                          <path d="m6 9 6 6 6-6" />
                        </svg>
                      </Link>
                      {openDropdown === item.label && item.dropdownItems && (
                        <div className="absolute top-full left-0 mt-0 w-64 bg-white border border-gray-200 shadow-lg z-50">
                          {item.dropdownItems.map((sub) => (
                            <Link
                              key={sub.label}
                              href={sub.href}
                              className="flex items-center gap-2 px-4 py-3 text-sm text-[#0F0F0F] hover:bg-[#CC0000] hover:text-white transition-colors border-b border-gray-100 last:border-0"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M7 7h10v10" />
                                <path d="M7 17 17 7" />
                              </svg>
                              {sub.label}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <Link
                      href={item.href}
                      className={`flex items-center gap-1 px-4 py-2 text-[15px] font-medium transition-colors ${
                        pathname === item.href
                          ? 'text-[#CC0000]'
                          : 'text-[#0F0F0F] hover:text-[#CC0000]'
                      }`}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <div className="flex items-center gap-3">
              <Link
                href="/careers"
                className="hidden lg:inline-flex items-center gap-2 bg-[#CC0000] text-white pl-5 pr-2 py-2 rounded-sm text-[15px] font-semibold hover:bg-[#AA0000] transition-colors group"
              >
                Work With Us
                <span className="bg-white/15 group-hover:bg-white/25 p-1.5 rounded-sm transition-colors">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M7 7h10v10" />
                    <path d="M7 17 17 7" />
                  </svg>
                </span>
              </Link>

              {/* Mobile menu button */}
              <button
                className="lg:hidden p-2 text-[#0F0F0F] hover:text-[#CC0000] transition-colors"
                onClick={() => setMobileOpen(!mobileOpen)}
                aria-label="Toggle menu"
              >
                {mobileOpen ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M4 12h16" />
                    <path d="M4 18h16" />
                    <path d="M4 6h16" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-gray-200 shadow-lg">
          <div className="px-4 py-4 space-y-1">
            <Link href="/" className="block px-4 py-3 text-[15px] font-medium text-[#0F0F0F] hover:text-[#CC0000] hover:bg-[#F8F9FA] transition-colors rounded-sm" onClick={() => setMobileOpen(false)}>Home</Link>

            {/* About Us mobile */}
            <div>
              <button
                className="w-full flex items-center justify-between px-4 py-3 text-[15px] font-medium text-[#0F0F0F] hover:text-[#CC0000] hover:bg-[#F8F9FA] transition-colors rounded-sm"
                onClick={() => setMobileAboutOpen(!mobileAboutOpen)}
              >
                About Us
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${mobileAboutOpen ? 'rotate-180' : ''}`} aria-hidden="true">
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>
              {mobileAboutOpen && (
                <div className="pl-4 space-y-1 mt-1">
                  <Link href="/about" className="block px-4 py-2 text-sm text-gray-600 hover:text-[#CC0000] transition-colors" onClick={() => setMobileOpen(false)}>About UNOC</Link>
                  <Link href="/about/mandate" className="block px-4 py-2 text-sm text-gray-600 hover:text-[#CC0000] transition-colors" onClick={() => setMobileOpen(false)}>Our Mandate</Link>
                  <Link href="/leadership" className="block px-4 py-2 text-sm text-gray-600 hover:text-[#CC0000] transition-colors" onClick={() => setMobileOpen(false)}>Leadership Team</Link>
                  <Link href="/about/subsidiaries" className="block px-4 py-2 text-sm text-gray-600 hover:text-[#CC0000] transition-colors" onClick={() => setMobileOpen(false)}>Subsidiaries</Link>
                </div>
              )}
            </div>

            {/* Our Business mobile */}
            <div>
              <button
                className="w-full flex items-center justify-between px-4 py-3 text-[15px] font-medium text-[#0F0F0F] hover:text-[#CC0000] hover:bg-[#F8F9FA] transition-colors rounded-sm"
                onClick={() => setMobileBusinessOpen(!mobileBusinessOpen)}
              >
                Our Business
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${mobileBusinessOpen ? 'rotate-180' : ''}`} aria-hidden="true">
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>
              {mobileBusinessOpen && (
                <div className="pl-4 space-y-1 mt-1">
                  <p className="px-4 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#CC0000]">Upstream</p>
                  <Link href="/projects" className="block px-4 py-2 text-sm text-gray-600 hover:text-[#CC0000] transition-colors" onClick={() => setMobileOpen(false)}>Exploration</Link>
                  <Link href="/projects/kingfisher" className="block px-4 py-2 text-sm text-gray-600 hover:text-[#CC0000] transition-colors" onClick={() => setMobileOpen(false)}>Kingfisher</Link>
                  <Link href="/projects/tilenga" className="block px-4 py-2 text-sm text-gray-600 hover:text-[#CC0000] transition-colors" onClick={() => setMobileOpen(false)}>Tilenga</Link>
                  <p className="px-4 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#CC0000] mt-2">Midstream</p>
                  <Link href="/projects/eacop" className="block px-4 py-2 text-sm text-gray-600 hover:text-[#CC0000] transition-colors" onClick={() => setMobileOpen(false)}>EACOP</Link>
                  <Link href="/projects/industrial-park" className="block px-4 py-2 text-sm text-gray-600 hover:text-[#CC0000] transition-colors" onClick={() => setMobileOpen(false)}>Industrial Park</Link>
                  <Link href="/projects/refinery" className="block px-4 py-2 text-sm text-gray-600 hover:text-[#CC0000] transition-colors" onClick={() => setMobileOpen(false)}>Refinery</Link>
                  <p className="px-4 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-[#CC0000] mt-2">Downstream</p>
                  <Link href="/projects/bulk-trading" className="block px-4 py-2 text-sm text-gray-600 hover:text-[#CC0000] transition-colors" onClick={() => setMobileOpen(false)}>Bulk Trading</Link>
                  <Link href="/projects/refinery" className="block px-4 py-2 text-sm text-gray-600 hover:text-[#CC0000] transition-colors" onClick={() => setMobileOpen(false)}>Uganda Oil Refinery</Link>
                </div>
              )}
            </div>

            {/* Sustainability mobile */}
            <div>
              <button
                className="w-full flex items-center justify-between px-4 py-3 text-[15px] font-medium text-[#0F0F0F] hover:text-[#CC0000] hover:bg-[#F8F9FA] transition-colors rounded-sm"
                onClick={() => setMobileSustainabilityOpen(!mobileSustainabilityOpen)}
              >
                Sustainability
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={`transition-transform ${mobileSustainabilityOpen ? 'rotate-180' : ''}`} aria-hidden="true">
                  <path d="m6 9 6 6 6-6" />
                </svg>
              </button>
              {mobileSustainabilityOpen && (
                <div className="pl-4 space-y-1 mt-1">
                  <Link href="/sustainability" className="block px-4 py-2 text-sm text-gray-600 hover:text-[#CC0000] transition-colors" onClick={() => setMobileOpen(false)}>Overview</Link>
                  <Link href="/sustainability/national-content" className="block px-4 py-2 text-sm text-gray-600 hover:text-[#CC0000] transition-colors" onClick={() => setMobileOpen(false)}>National Content</Link>
                  <Link href="/sustainability/hse" className="block px-4 py-2 text-sm text-gray-600 hover:text-[#CC0000] transition-colors" onClick={() => setMobileOpen(false)}>Health, Safety & Environment</Link>
                  <Link href="/sustainability/environment" className="block px-4 py-2 text-sm text-gray-600 hover:text-[#CC0000] transition-colors" onClick={() => setMobileOpen(false)}>Environment & ESG</Link>
                  <Link href="/sustainability/community" className="block px-4 py-2 text-sm text-gray-600 hover:text-[#CC0000] transition-colors" onClick={() => setMobileOpen(false)}>Community Impact</Link>
                </div>
              )}
            </div>

            <Link href="/news" className="block px-4 py-3 text-[15px] font-medium text-[#0F0F0F] hover:text-[#CC0000] hover:bg-[#F8F9FA] transition-colors rounded-sm" onClick={() => setMobileOpen(false)}>Media</Link>

            <Link
              href="/careers"
              className="block mt-4 text-center bg-[#CC0000] text-white px-6 py-3 rounded-sm font-semibold hover:bg-[#AA0000] transition-colors"
              onClick={() => setMobileOpen(false)}
            >
              Work With Us
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
